import React, { useState, useEffect, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, AreaChart, Area } from 'recharts';
import fetchData from '../../functions/fetchData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './CashDrawerReport.css';

function CashDrawerReport() {
  const [cashDrawerData, setCashDrawerData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalDrawers: 0,
    totalCashHandled: 0,
    averageVariance: 0,
    perfectMatches: 0,
    overages: 0,
    shortages: 0,
    largestVariance: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchCashDrawerData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch cash drawer data
      const drawerData = await fetchData('cash_drawer', null, 'open_date', {});
      
      if (drawerData && Array.isArray(drawerData)) {
        // Filter data for selected month and year
        const filteredData = drawerData.filter(drawer => {
          const drawerDate = new Date(drawer.open_date);
          return drawerDate.getMonth() + 1 === selectedMonth && 
                 drawerDate.getFullYear() === selectedYear;
        });
        
        // Process data for charts
        const processedData = filteredData.map(drawer => {
          const openingCash = parseFloat(drawer.opening_cash) || 0;
          const closingCash = parseFloat(drawer.closing_cash) || 0;
          const expectedCash = parseFloat(drawer.expected_cash) || 0;
          const cashDifference = parseFloat(drawer.cash_difference) || 0;
          const cashIn = parseFloat(drawer.cash_in) || 0;
          const cashOut = parseFloat(drawer.cash_out) || 0;
          
          return {
            date: new Date(drawer.open_date).getDate(),
            fullDate: drawer.open_date,
            openingCash,
            closingCash,
            expectedCash,
            actualCash: closingCash,
            variance: cashDifference,
            cashIn,
            cashOut,
            totalCashFlow: openingCash + cashIn - cashOut,
            closedBy: drawer.closed_by,
            status: drawer.status,
            notes: drawer.notes
          };
        });

        setCashDrawerData(processedData.sort((a, b) => a.date - b.date));
        
        // Calculate summary statistics
        const totalDrawers = processedData.length;
        const totalCashHandled = processedData.reduce((sum, drawer) => sum + drawer.expectedCash, 0);
        const totalVariance = processedData.reduce((sum, drawer) => sum + Math.abs(drawer.variance), 0);
        const averageVariance = totalDrawers > 0 ? totalVariance / totalDrawers : 0;
        
        const perfectMatches = processedData.filter(drawer => Math.abs(drawer.variance) < 0.01).length;
        const overages = processedData.filter(drawer => drawer.variance > 0).length;
        const shortages = processedData.filter(drawer => drawer.variance < 0).length;
        
        const largestVariance = processedData.reduce((max, drawer) => 
          Math.abs(drawer.variance) > Math.abs(max) ? drawer.variance : max, 0);
        
        setSummaryData({
          totalDrawers,
          totalCashHandled,
          averageVariance,
          perfectMatches,
          overages,
          shortages,
          largestVariance
        });
      } else {
        setCashDrawerData([]);
        setSummaryData({
          totalDrawers: 0,
          totalCashHandled: 0,
          averageVariance: 0,
          perfectMatches: 0,
          overages: 0,
          shortages: 0,
          largestVariance: 0
        });
      }
    } catch (error) {
      console.error('Error fetching cash drawer data:', error);
      setError('Failed to load cash drawer data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchCashDrawerData();
  }, [fetchCashDrawerData]);

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const generateMonthOptions = () => {
    const months = [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];
    return months;
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 3; i--) {
      years.push(i);
    }
    return years;
  };

  const formatCurrency = (amount) => {
    return `฿${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Calculate table summaries
  const calculateTableSummary = () => {
    if (cashDrawerData.length === 0) return null;
    
    return {
      totalOpeningCash: cashDrawerData.reduce((sum, drawer) => sum + drawer.openingCash, 0),
      totalExpectedCash: cashDrawerData.reduce((sum, drawer) => sum + drawer.expectedCash, 0),
      totalActualCash: cashDrawerData.reduce((sum, drawer) => sum + drawer.actualCash, 0),
      totalVariance: cashDrawerData.reduce((sum, drawer) => sum + drawer.variance, 0),
      totalCashIn: cashDrawerData.reduce((sum, drawer) => sum + drawer.cashIn, 0),
      totalCashOut: cashDrawerData.reduce((sum, drawer) => sum + drawer.cashOut, 0),
      recordCount: cashDrawerData.length
    };
  };



  if (isLoading) {
    return (
      <Layout>
        <Header title="Cash Drawer Report" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading cash drawer report...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Cash Drawer Report" />
      <div className="cash-drawer-container">
        
        {/* Controls */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="monthSelect">Select Month:</label>
              <select
                id="monthSelect"
                className="form-control"
                value={selectedMonth}
                onChange={handleMonthChange}
              >
                {generateMonthOptions().map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="yearSelect">Select Year:</label>
              <select
                id="yearSelect"
                className="form-control"
                value={selectedYear}
                onChange={handleYearChange}
              >
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button 
              className="btn btn-outline-primary"
              onClick={fetchCashDrawerData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card total-drawers">
              <div className="card-icon">
                <i className="fas fa-cash-register"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalDrawers}</h3>
                <p>Total Drawers Closed</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card total-cash">
              <div className="card-icon">
                <i className="fas fa-money-bill-wave"></i>
              </div>
              <div className="card-info">
                <h3>{formatCurrency(summaryData.totalCashHandled)}</h3>
                <p>Total Cash Handled</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card avg-variance">
              <div className="card-icon">
                <i className="fas fa-balance-scale"></i>
              </div>
              <div className="card-info">
                <h3 className={summaryData.averageVariance >= 0 ? 'text-success' : 'text-danger'}>
                  {formatCurrency(summaryData.averageVariance)}
                </h3>
                <p>Average Variance</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card perfect-matches">
              <div className="card-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.perfectMatches}</h3>
                <p>Perfect Matches</p>
              </div>
            </div>
          </div>

          <div className="col-lg-2-4 col-md-6 mb-3">
            <div className="summary-card largest-variance">
              <div className="card-icon">
                <i className={`fas ${summaryData.largestVariance >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
              </div>
              <div className="card-info">
                <h3 className={summaryData.largestVariance >= 0 ? 'text-success' : 'text-danger'}>
                  {formatCurrency(summaryData.largestVariance)}
                </h3>
                <p>Largest Variance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row">
          {/* Cash Variance Chart */}
          <div className="col-lg-8 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Daily Cash Variance - {generateMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}</h5>
              </div>
              <div className="panel-content">
                {cashDrawerData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={cashDrawerData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                        domain={['dataMin - 10', 'dataMax + 10']}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name === 'variance' ? 'Cash Variance' : name
                        ]}
                        labelFormatter={(label) => `Day ${label}`}
                      />
                      <Bar 
                        dataKey="variance" 
                        name="variance"
                        radius={[4, 4, 0, 0]}
                      >
                        {cashDrawerData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.variance >= 0 ? '#27ae60' : '#e74c3c'}
                            stroke={entry.variance >= 0 ? '#2ecc71' : '#c0392b'}
                            strokeWidth={1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-bar"></i>
                    <p>No cash drawer data available for {generateMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cash Flow Trend */}
          <div className="col-lg-4 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Cash Flow Trend</h5>
              </div>
              <div className="panel-content">
                {cashDrawerData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart 
                      data={cashDrawerData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3498db" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3498db" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        stroke="#666"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => [formatCurrency(value), 'Total Cash Flow']}
                        labelFormatter={(label) => `Day ${label}`}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="totalCashFlow" 
                        stroke="#3498db" 
                        strokeWidth={3}
                        fill="url(#cashFlowGradient)"
                        dot={{ fill: '#3498db', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 7, stroke: '#3498db', strokeWidth: 2, fill: '#fff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-line"></i>
                    <p>No cash flow data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Detailed Cash Drawer Report - {generateMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}</h5>
              </div>
              <div className="panel-content" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div className="table-responsive" style={{ minWidth: '100%', overflowX: 'auto' }}>
                  <table className="table table-striped" style={{ minWidth: '1000px' }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Opening Cash</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Expected Cash</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Actual Cash</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Variance</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Cash In</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Cash Out</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Closed By</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashDrawerData.length > 0 ? (
                        <>
                          {cashDrawerData.map((drawer, index) => (
                            <tr key={index}>
                              <td className="font-weight-bold">{drawer.fullDate}</td>
                              <td>{formatCurrency(drawer.openingCash)}</td>
                              <td>{formatCurrency(drawer.expectedCash)}</td>
                              <td>{formatCurrency(drawer.actualCash)}</td>
                              <td className={drawer.variance >= 0 ? 'text-success' : 'text-danger'}>
                                {formatCurrency(drawer.variance)}
                              </td>
                              <td className="text-success">{formatCurrency(drawer.cashIn)}</td>
                              <td className="text-danger">{formatCurrency(drawer.cashOut)}</td>
                              <td>{drawer.closedBy || 'N/A'}</td>
                              <td>
                                <span className={`badge ${drawer.status === 'closed' ? 'bg-success' : 'bg-warning'}`}>
                                  {drawer.status}
                                </span>
                              </td>
                              <td>{drawer.notes || 'No notes'}</td>
                            </tr>
                          ))}
                          {/* Summary Row */}
                          {(() => {
                            const summary = calculateTableSummary();
                            return summary ? (
                              <tr className="table-summary-row">
                                <td className="font-weight-bold text-primary">
                                  <strong>TOTAL ({summary.recordCount} records)</strong>
                                </td>
                                <td className="font-weight-bold text-primary">
                                  <strong>{formatCurrency(summary.totalOpeningCash)}</strong>
                                </td>
                                <td className="font-weight-bold text-primary">
                                  <strong>{formatCurrency(summary.totalExpectedCash)}</strong>
                                </td>
                                <td className="font-weight-bold text-primary">
                                  <strong>{formatCurrency(summary.totalActualCash)}</strong>
                                </td>
                                <td className={`font-weight-bold ${summary.totalVariance >= 0 ? 'text-success' : 'text-danger'}`}>
                                  <strong>{formatCurrency(summary.totalVariance)}</strong>
                                </td>
                                <td className="font-weight-bold text-success">
                                  <strong>{formatCurrency(summary.totalCashIn)}</strong>
                                </td>
                                <td className="font-weight-bold text-danger">
                                  <strong>{formatCurrency(summary.totalCashOut)}</strong>
                                </td>
                                <td className="text-muted">-</td>
                                <td className="text-muted">-</td>
                                <td className="text-muted">Summary Row</td>
                              </tr>
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center text-muted">
                            No data available for {generateMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default CashDrawerReport;